/**
 * Practice level schema — SpeedRun layout plus balls, mechanics toggles, and guide steps.
 */
import {
    normalizeLevelData,
    type SpeedrunLevelDef,
} from "./speedrun-levels";

export const PRACTICE_CATEGORIES = [
    "AIM",
    "Movement",
    "Dodge",
    "Color Rush",
    "SpeedRun",
    "Basics",
    "Advanced",
] as const;

export type PracticeCategory = (typeof PRACTICE_CATEGORIES)[number];

export type PracticeBallDef = {
    nx: number;
    ny: number;
    /** Direction angle in degrees (0 = right, 90 = down). */
    angleDeg: number;
    /** Normalized speed multiplier (0.25–3). */
    speed: number;
    /** Radius in px relative to a 700px-tall canvas reference (~default ball). */
    radius: number;
};

export type PracticeMechanics = {
    colorRush?: boolean;
    aim?: boolean;
    wasd?: boolean;
    magnet?: boolean;
    progression?: boolean;
    bounceBoost?: boolean;
    ballCollisions?: boolean;
};

export type PracticeGuideMessageSide = "top" | "bottom" | "left" | "right";

export type PracticeGuideZoneDef = {
    nx: number;
    ny: number;
    nw: number;
    nh: number;
};

export type PracticeGuideStepDef = {
    /** Zone the player must enter to activate this step (and reveal the next). */
    zone: PracticeGuideZoneDef;
    message?: string;
    messageSide?: PracticeGuideMessageSide;
};

export type PracticeLevelDef = SpeedrunLevelDef & {
    balls?: PracticeBallDef[];
    mechanics?: PracticeMechanics;
    guideSteps?: PracticeGuideStepDef[];
};

export type PracticeLevelMeta = {
    levelIndex: number;
    displayName: string | null;
    description: string | null;
    category: PracticeCategory | null;
    authorName: string | null;
    viewCount: number;
};

function clamp(v: number, lo: number, hi: number) {
    return Math.max(lo, Math.min(hi, v));
}

function normZone(raw: unknown): PracticeGuideZoneDef | null {
    if (!raw || typeof raw !== "object") return null;
    const o = raw as Record<string, unknown>;
    const nx = Number(o.nx);
    const ny = Number(o.ny);
    const nw = Number(o.nw);
    const nh = Number(o.nh);
    if (![nx, ny, nw, nh].every(Number.isFinite)) return null;
    return {
        nx: clamp(nx, 0, 1),
        ny: clamp(ny, 0, 1),
        nw: clamp(nw, 0.02, 1),
        nh: clamp(nh, 0.02, 1),
    };
}

function pickMessageSide(z: PracticeGuideZoneDef): PracticeGuideMessageSide {
    const spaceTop = z.ny;
    const spaceBottom = 1 - (z.ny + z.nh);
    const spaceLeft = z.nx;
    const spaceRight = 1 - (z.nx + z.nw);
    const best = Math.max(spaceTop, spaceBottom, spaceLeft, spaceRight);
    if (best === spaceTop) return "top";
    if (best === spaceBottom) return "bottom";
    if (best === spaceLeft) return "left";
    return "right";
}

export function autoMessageSide(z: PracticeGuideZoneDef): PracticeGuideMessageSide {
    return pickMessageSide(z);
}

export function isPracticeCategory(v: unknown): v is PracticeCategory {
    return typeof v === "string" && (PRACTICE_CATEGORIES as readonly string[]).includes(v);
}

export function emptyPracticeLevel(): PracticeLevelDef {
    return {
        spawn: { nx: 0.12, ny: 0.5 },
        goal: { nx: 0.88, ny: 0.5, r: 0.04 },
        staticSegs: [],
        moving: [],
        balls: [],
        mechanics: {},
        guideSteps: [],
    };
}

export function normalizePracticeLevelData(raw: unknown): PracticeLevelDef {
    const base = normalizeLevelData(raw && typeof raw === "object" ? raw : {});
    const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;

    const balls: PracticeBallDef[] = [];
    if (Array.isArray(o.balls)) {
        for (const b of o.balls) {
            if (!b || typeof b !== "object") continue;
            const r = b as Record<string, unknown>;
            const nx = Number(r.nx);
            const ny = Number(r.ny);
            if (!Number.isFinite(nx) || !Number.isFinite(ny)) continue;
            balls.push({
                nx: clamp(nx, 0, 1),
                ny: clamp(ny, 0, 1),
                angleDeg: Number.isFinite(Number(r.angleDeg)) ? Number(r.angleDeg) % 360 : 0,
                speed: clamp(Number(r.speed) || 1, 0.25, 3),
                radius: clamp(Number(r.radius) || 14, 6, 48),
            });
        }
    }

    const mechanicsRaw = o.mechanics && typeof o.mechanics === "object" ? (o.mechanics as Record<string, unknown>) : {};
    const mechanics: PracticeMechanics = {
        colorRush: !!mechanicsRaw.colorRush,
        aim: !!mechanicsRaw.aim,
        wasd: !!mechanicsRaw.wasd,
        magnet: !!mechanicsRaw.magnet,
        progression: !!mechanicsRaw.progression,
        bounceBoost: mechanicsRaw.bounceBoost !== false,
        ballCollisions: !!mechanicsRaw.ballCollisions,
    };

    const guideSteps: PracticeGuideStepDef[] = [];
    if (Array.isArray(o.guideSteps)) {
        for (const step of o.guideSteps) {
            if (!step || typeof step !== "object") continue;
            const s = step as Record<string, unknown>;
            const zone = normZone(s.zone);
            if (!zone) continue;
            const sideRaw = s.messageSide;
            const messageSide: PracticeGuideMessageSide =
                sideRaw === "top" || sideRaw === "bottom" || sideRaw === "left" || sideRaw === "right"
                    ? sideRaw
                    : pickMessageSide(zone);
            guideSteps.push({
                zone,
                message: typeof s.message === "string" ? s.message.slice(0, 500) : "",
                messageSide,
            });
        }
    }

    return {
        ...base,
        balls,
        mechanics,
        guideSteps,
    };
}

export function practiceLevelToJson(def: PracticeLevelDef): object {
    return {
        spawn: def.spawn,
        goal: def.goal,
        staticSegs: def.staticSegs,
        moving: def.moving,
        points: def.points && def.points.length ? def.points : undefined,
        winds: def.winds && def.winds.length ? def.winds : undefined,
        texts: def.texts && def.texts.length ? def.texts : undefined,
        ...(def.checkpoints?.length ? { checkpoints: def.checkpoints } : {}),
        ...(def.temporalSpawn ? { temporalSpawn: def.temporalSpawn } : {}),
        ...(def.portals?.length ? { portals: def.portals } : {}),
        balls: def.balls?.length ? def.balls : [],
        mechanics: def.mechanics ?? {},
        guideSteps: def.guideSteps?.length ? def.guideSteps : [],
    };
}

export function pointInGuideZone(nx: number, ny: number, z: PracticeGuideZoneDef): boolean {
    return nx >= z.nx && nx <= z.nx + z.nw && ny >= z.ny && ny <= z.ny + z.nh;
}

const runtimeLevels = new Map<number, PracticeLevelDef>();
const runtimeMeta = new Map<number, PracticeLevelMeta>();

let playtestOverride: PracticeLevelDef | null = null;

export function setPracticePlaytestOverride(def: PracticeLevelDef | null) {
    playtestOverride = def ? normalizePracticeLevelData(def) : null;
}

export function getPracticePlaytestOverride(): PracticeLevelDef | null {
    return playtestOverride;
}

export function setRuntimePracticeLevelsFromPayload(
    rows: { levelIndex: number; data: unknown; displayName?: string | null; description?: string | null; category?: string | null; authorName?: string | null; viewCount?: number }[],
) {
    runtimeLevels.clear();
    runtimeMeta.clear();
    for (const row of rows) {
        const idx = Math.floor(Number(row.levelIndex));
        if (!Number.isInteger(idx) || idx < 1) continue;
        runtimeLevels.set(idx, normalizePracticeLevelData(row.data));
        runtimeMeta.set(idx, {
            levelIndex: idx,
            displayName: row.displayName ?? null,
            description: row.description ?? null,
            category: isPracticeCategory(row.category) ? row.category : null,
            authorName: row.authorName ?? null,
            viewCount: Math.max(0, Math.floor(Number(row.viewCount) || 0)),
        });
    }
}

export function getRuntimePracticeLevel(levelIndex: number): PracticeLevelDef | null {
    if (playtestOverride) return playtestOverride;
    return runtimeLevels.get(levelIndex) ?? null;
}

export function getRuntimePracticeMeta(levelIndex: number): PracticeLevelMeta | null {
    return runtimeMeta.get(levelIndex) ?? null;
}

export function listRuntimePracticeLevels(): PracticeLevelMeta[] {
    return [...runtimeMeta.values()].sort((a, b) => a.levelIndex - b.levelIndex);
}

export function listRuntimePracticeLevelIndices(): number[] {
    return [...runtimeLevels.keys()].sort((a, b) => a - b);
}
